/* * Version: 3.7.0
 * Letzte Änderung: 16.09.2026, 15:00
 * Änderungshistorie: Fehler behoben, bei dem "undefined undefined" im Modal stand. Das Modal erhält nun das vollständig aufgeladene Nennungs-Objekt aus dem entries-State. Code formatiert zur besseren Wartbarkeit.
 * Copyright: BAIT-Tech
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ScoreEntryModal from '../components/ScoreEntryModal';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-toastify';

const getLaneOrder = (startLane) => {
  switch (startLane) {
    case 1: return [1, 2, 4, 3];
    case 2: return [2, 1, 3, 4];
    case 3: return [3, 4, 2, 1];
    case 4: return [4, 3, 1, 2];
    default: return [1, 2, 3, 4];
  }
};

const ScheduleScreen = () => {
  const { id: tournamentId } = useParams();
  const [tournament, setTournament] = useState(null);
  const [entries, setEntries] = useState(null);
  const [teams, setTeams] = useState(null);
  const [scores, setScores] = useState(null);
  const [scheduleData, setScheduleData] = useState([]);
  const [columns, setColumns] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPlayerNamesInPdf, setShowPlayerNamesInPdf] = useState(false);
  const [showSignatureInPdf, setShowSignatureInPdf] = useState(false);
  const [collapsedTeams, setCollapsedTeams] = useState({});
  const [collapsedDays, setCollapsedDays] = useState({});
  const [dayFilter, setDayFilter] = useState('all');
  const [exportOnlyOccupied, setExportOnlyOccupied] = useState(false);
  const [selectedExportDays, setSelectedExportDays] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [teamFilter, setTeamFilter] = useState('all');

  const { dynamicTimeSlots, dynamicLanes } = useMemo(() => {
    if (!tournament) return { dynamicTimeSlots: [], dynamicLanes: [] };
    const lanes = Array.from({ length: tournament.numberOfLanes || 0 }, (_, i) => i + 1);
    const timeSlots = [];
    (tournament.scheduleBlocks || []).forEach(block => {
      if (!block.date || !block.startTime || !block.endTime) return;
      const [startH, startM] = block.startTime.split(':').map(Number);
      const [endH, endM] = block.endTime.split(':').map(Number);
      const dateString = block.date.substring(0, 10);
      const [year, month, day] = dateString.split('-').map(Number);
      const startTime = new Date(Date.UTC(year, month - 1, day, startH, startM));
      const endTime = new Date(Date.UTC(year, month - 1, day, endH, endM));
      for (let time = new Date(startTime); time < endTime; time.setMinutes(time.getMinutes() + block.interval)) { 
        timeSlots.push(new Date(time).toISOString()); 
      }
    });
    timeSlots.sort();
    return { dynamicTimeSlots: timeSlots, dynamicLanes: lanes };
  }, [tournament]);

  const groupedTimeSlots = useMemo(() => {
    const groups = new Map();
    dynamicTimeSlots.forEach((time, originalIndex) => {
      const dateKey = new Date(time).toLocaleDateString('en-CA', { timeZone: 'UTC' }); 
      if (!groups.has(dateKey)) { groups.set(dateKey, []); }
      groups.get(dateKey).push({ time, originalIndex });
    });
    return groups;
  }, [dynamicTimeSlots]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const [tournamentRes, entriesRes, scheduleRes, scoresRes, teamsRes] = await Promise.all([
          apiClient.get(`/api/tournaments/${tournamentId}`),
          apiClient.get(`/api/tournaments/${tournamentId}/entries`),
          apiClient.get(`/api/tournaments/${tournamentId}/schedule`),
          apiClient.get(`/api/tournaments/${tournamentId}/scores`),
          apiClient.get(`/api/tournaments/${tournamentId}/teams`),
        ]);
        setTournament(tournamentRes.data);
        setEntries(entriesRes.data.reduce((acc, entry) => { acc[entry._id] = entry; return acc; }, {}));
        setScheduleData(scheduleRes.data);
        setScores(scoresRes.data.reduce((acc, score) => { if (score.entry?._id) acc[score.entry._id] = score; return acc; }, {}));
        setTeams(teamsRes.data.reduce((acc, team) => { acc[team._id] = team; return acc; }, {}));
        const availableDays = new Set((tournamentRes.data.scheduleBlocks || []).map(b => new Date(b.date).toLocaleDateString('en-CA', { timeZone: 'UTC' })));
        setSelectedExportDays(Array.from(availableDays));
      } catch (err) { 
        toast.error("Fehler beim Laden der Daten."); 
      } finally { 
        setLoading(false); 
      }
    };
    fetchAllData();
  }, [tournamentId]);

  useEffect(() => {
    if (!entries || !tournament || !teams) return;
    const scheduledEntryIds = new Set(scheduleData.filter(s => s.entry).map(s => s.entry._id));
    const unassignedItems = [];
    Object.values(teams).forEach(team => {
      const teamEntries = Object.values(entries).filter(e => e.team?._id === team._id);
      const scheduledTeamEntries = teamEntries.filter(e => scheduledEntryIds.has(e._id));
      if (scheduledTeamEntries.length === 0 && teamEntries.length > 0) { 
        unassignedItems.push(`team-${team._id}`); 
      } else if (scheduledTeamEntries.length < teamEntries.length) { 
        teamEntries.forEach(entry => { 
          if (!scheduledEntryIds.has(entry._id)) { unassignedItems.push(`entry-${entry._id}`); } 
        }); 
      }
    });
    Object.values(entries).forEach(entry => { 
      if (!entry.team && !scheduledEntryIds.has(entry._id)) { unassignedItems.push(`entry-${entry._id}`); } 
    });
    const initialColumns = { 'unassigned': { id: 'unassigned', title: 'Unplatzierte Nennungen', itemIds: unassignedItems }};
    dynamicTimeSlots.forEach((time, timeIndex) => {
      dynamicLanes.forEach(lane => {
        const slotId = `slot-${timeIndex}-${lane}`;
        const scheduled = scheduleData.find(s => s.entry && new Date(s.startTime).toISOString() === time && s.startLane === lane);
        initialColumns[slotId] = { id: slotId, title: `Slot ${time}-${lane}`, entryIds: scheduled ? [scheduled.entry._id] : [] };
      });
    });
    setColumns(initialColumns);
  }, [dynamicTimeSlots, dynamicLanes, entries, scheduleData, tournament, teams]);
  
  const filteredEntryIds = useMemo(() => {
    if (!entries) return new Set();
    const lowerCaseSearchTerm = searchTerm.toLowerCase().trim();
    if (!lowerCaseSearchTerm && teamFilter === 'all') { return new Set(Object.keys(entries)); }
    const result = new Set();
    Object.values(entries).forEach(entry => {
      if (!entry || !entry.player) return;
      const teamMatch = teamFilter === 'all' || entry.team?._id === teamFilter;
      const name = `${entry.player.firstName} ${entry.player.lastName}`.toLowerCase();
      const searchMatch = !lowerCaseSearchTerm || name.includes(lowerCaseSearchTerm);
      if (teamMatch && searchMatch) { result.add(entry._id); }
    });
    return result;
  }, [searchTerm, teamFilter, entries]);

  const toggleTeamCollapse = (teamId) => { setCollapsedTeams(prev => ({ ...prev, [teamId]: !prev[teamId] })); };
  const toggleDayCollapse = (dateKey) => { setCollapsedDays(prev => ({ ...prev, [dateKey]: !prev[dateKey] })); };
  
  const handleSaveScore = async (entryId, tournamentId, gameMode, phase, sets) => {
    try {
      const payload = { entryId, tournamentId, gameMode, phase, sets };
      const { data: savedScore } = await apiClient.post('/api/scores', payload);
      setScores(prev => ({ ...prev, [entryId]: savedScore }));
      toast.success("Ergebnis gespeichert!");
      setModalData(null);
    } catch (err) { 
      toast.error("Fehler beim Speichern des Ergebnisses.");
    }
  };

  const handlePlayerNameUpdate = (scheduleEntryId, updatedPlayer) => {
    setEntries(prevEntries => {
      const targetEntryId = Object.keys(prevEntries).find(key => prevEntries[key].player && prevEntries[key].player._id === updatedPlayer._id);
      if (!targetEntryId) return prevEntries;
      const updatedEntry = { ...prevEntries[targetEntryId], player: updatedPlayer };
      return { ...prevEntries, [targetEntryId]: updatedEntry };
    });
    setScheduleData(prevScheduleData => {
      return prevScheduleData.map(scheduleItem => {
        if (scheduleItem.entry?.player?._id === updatedPlayer._id) { return { ...scheduleItem, entry: { ...scheduleItem.entry, player: updatedPlayer } }; }
        return scheduleItem;
      });
    });
    toast.success("Spielername aktualisiert!");
  };

  const handleEntryUpdate = (updatedEntry) => { 
    setEntries(prevEntries => ({ ...prevEntries, [updatedEntry._id]: updatedEntry, })); 
    toast.success("Wertung aktualisiert!"); 
  };
  
  const handleTeamUpdate = (updatedTeam) => {
    setTeams(prevTeams => ({ ...prevTeams, [updatedTeam._id]: updatedTeam, }));
    setEntries(prevEntries => {
      const newEntries = { ...prevEntries };
      Object.keys(newEntries).forEach(entryId => { 
        if (newEntries[entryId].team?._id === updatedTeam._id) { 
          newEntries[entryId] = { ...newEntries[entryId], team: updatedTeam }; 
        } 
      });
      return newEntries;
    });
    setScheduleData(prevScheduleData => {
      return prevScheduleData.map(scheduleItem => {
        if (scheduleItem.entry?.team?._id === updatedTeam._id) { 
          return { ...scheduleItem, entry: { ...scheduleItem.entry, team: updatedTeam } }; 
        }
        return scheduleItem;
      });
    });
    toast.success("Mannschafts-Wertung aktualisiert!");
  };

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (draggableId.startsWith('team-')) {
      const teamId = draggableId.split('-')[1];
      const teamEntries = Object.values(entries).filter(e => e.team?._id === teamId);
      const parts = destination.droppableId.split('-');
      if (parts[0] !== 'slot') return toast.info("Mannschaften können nur auf den Startplan gezogen werden.");
      const timeIndex = parseInt(parts[1], 10);
      const startLane = parseInt(parts[2], 10);
      const placementPattern = { 1: [{t:0,l:1}, {t:0,l:3}, {t:1,l:2}, {t:1,l:4}], 2: [{t:0,l:2}, {t:0,l:4}, {t:1,l:1}, {t:1,l:3}], 3: [{t:0,l:3}, {t:0,l:1}, {t:1,l:4}, {t:1,l:2}], 4: [{t:0,l:4}, {t:0,l:2}, {t:1,l:3}, {t:1,l:1}], };
      const pattern = placementPattern[startLane];
      if (!pattern) return toast.error("Ungültiges Platzierungsmuster.");
      const targetSlots = [];
      for (let i = 0; i < teamEntries.length; i++) {
        const targetTimeIndex = timeIndex + pattern[i].t;
        const targetLane = pattern[i].l;
        if (targetTimeIndex >= dynamicTimeSlots.length) return toast.error("Nicht genügend nachfolgende Startzeiten.");
        const slotId = `slot-${targetTimeIndex}-${targetLane}`;
        if (!columns[slotId] || columns[slotId].entryIds.length > 0) return toast.error(`Ziel-Slot (Bahn ${targetLane}, ${formatTime(dynamicTimeSlots[targetTimeIndex])} Uhr) ist belegt.`);
        targetSlots.push({ entry: teamEntries[i], time: dynamicTimeSlots[targetTimeIndex], lane: targetLane });
      }
      try {
        const promises = targetSlots.map(ts => apiClient.post(`/api/tournaments/${tournamentId}/schedule`, { entryId: ts.entry._id, startTime: ts.time, startLane: ts.lane }));
        const responses = await Promise.all(promises);
        setScheduleData(prev => [...prev, ...responses.map(res => res.data)]);
        toast.success(`Mannschaft ${teams[teamId].name} erfolgreich platziert!`);
      } catch(err) { 
        toast.error("Fehler beim Platzieren der Mannschaft."); 
      }
      return;
    }
    if (draggableId.startsWith('entry-')) {
      const entryId = draggableId.split('-')[1];
      if (!entryId) return;
      const startColumn = columns[source.droppableId];
      const finishColumn = columns[destination.droppableId];
      if (!startColumn || !finishColumn || source.droppableId === destination.droppableId) return;
      if (finishColumn.id.startsWith('slot-') && finishColumn.entryIds.length > 0) return toast.error("Dieser Slot ist bereits belegt.");
      const newColumns = JSON.parse(JSON.stringify(columns));
      const sourceList = source.droppableId === 'unassigned' ? newColumns.unassigned.itemIds : newColumns[source.droppableId].entryIds;
      const [movedItem] = sourceList.splice(source.index, 1);
      const destList = destination.droppableId === 'unassigned' ? newColumns.unassigned.itemIds : newColumns[destination.droppableId].entryIds;
      const destItem = destination.droppableId.startsWith('slot-') ? entryId : movedItem;
      destList.splice(destination.index, 0, destItem);
      setColumns(newColumns);
      const scheduleEntry = scheduleData.find(s => s.entry?._id === entryId);
      if (finishColumn.id.startsWith('slot-')) {
          const parts = finishColumn.id.split('-');
          const timeIndex = parseInt(parts[1], 10);
          const lane = parseInt(parts[2], 10);
          const time = dynamicTimeSlots[timeIndex];
          apiClient.post(`/api/tournaments/${tournamentId}/schedule`, { entryId, startTime: time, startLane: lane }).then(res => { 
            setScheduleData(prev => [...prev.filter(s => s.entry?._id !== entryId), res.data]); 
            toast.success("Position gespeichert."); 
          }).catch(() => toast.error("Speichern fehlgeschlagen."));
      } else if (finishColumn.id === 'unassigned' && scheduleEntry) {
          apiClient.delete(`/api/schedule/${scheduleEntry._id}`).then(() => { 
            setScheduleData(prev => prev.filter(s => s._id !== scheduleEntry._id)); 
            toast.info("Nennung aus Startplan entfernt."); 
          }).catch(() => toast.error("Entfernen fehlgeschlagen."));
      }
    }
  };

  const formatTime = (isoString) => new Date(isoString).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
  const formatDateHeader = (dateKey) => new Date(dateKey + 'T00:00:00.000Z').toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
  const handleExportDayChange = (dateKey) => { setSelectedExportDays(prev => prev.includes(dateKey) ? prev.filter(d => d !== dateKey) : [...prev, dateKey]); };
  const getAffiliationText = (entry) => { 
    if (!entry || !entry.player) return 'Vereinslos'; 
    if (entry.team?._id && teams && teams[entry.team._id]) { return teams[entry.team._id].name || 'Team'; } 
    if (entry.player.club) return entry.player.club.name; 
    if (entry.player.guestClubName) return entry.player.guestClubName; 
    return 'Vereinslos'; 
  };
  
  const exportScheduleToPDF = () => { 
    if(!tournament || !columns || !entries) return; 
    const doc = new jsPDF(); 
    doc.setFont('helvetica', 'bold'); 
    doc.setFontSize(16); 
    doc.text(`Startplan: ${tournament.name}`, doc.internal.pageSize.getWidth() / 2, 16, { align: 'center' }); 
    let yPos = 25; 
    const daysToExport = Array.from(groupedTimeSlots.entries()).filter(([dateKey]) => selectedExportDays.includes(dateKey)); 
    daysToExport.forEach(([dateKey, slots]) => { 
      const head = [['Zeit', ...dynamicLanes.map(lane => `Bahn ${lane}`)]]; 
      let body = slots.map(({ time, originalIndex }) => { 
        const row = [formatTime(time)]; 
        let isRowOccupied = false; 
        dynamicLanes.forEach(lane => { 
          const slotId = `slot-${originalIndex}-${lane}`; 
          const column = columns[slotId]; 
          if (column && column.entryIds.length > 0 && entries[column.entryIds[0]] && entries[column.entryIds[0]].player) { 
            isRowOccupied = true; 
            const entry = entries[column.entryIds[0]]; 
            const teamOrClub = getAffiliationText(entry); 
            if (showPlayerNamesInPdf) { 
              row.push(`${entry.player.lastName}, ${entry.player.firstName}\n${teamOrClub}`); 
            } else { 
              row.push(teamOrClub); 
            } 
          } else { 
            row.push(''); 
          } 
        }); 
        return { row, isOccupied: isRowOccupied }; 
      }); 
      if (exportOnlyOccupied) { body = body.filter(item => item.isOccupied); } 
      if (body.length === 0) return; 
      const finalBody = body.map(item => item.row); 
      const tableHeight = (finalBody.length + 1) * 8 + 15; 
      if (yPos + tableHeight > doc.internal.pageSize.height - 15) { doc.addPage(); yPos = 15; } 
      doc.setFontSize(12); 
      doc.setFont('helvetica', 'bold'); 
      doc.text(formatDateHeader(dateKey), 14, yPos); 
      yPos += 7; 
      autoTable(doc, { startY: yPos, head: head, body: finalBody, theme: 'grid', headStyles: { halign: 'center', fillColor: [220, 220, 220], textColor: 20, fontStyle: 'bold' }, columnStyles: { 0: { halign: 'center', cellWidth: 25 } }, styles: { valign: 'middle' }, }); 
      yPos = doc.lastAutoTable.finalY + 15; 
    }); 
    doc.save(`Startplan_${tournament.name.replace(/\s/g, '_')}.pdf`); 
  };
  
  const drawScoreSheet = (doc, entry, startLane, time, startY, options = {}) => { 
    const { isSemiBlanko = false } = options; 
    const isBlanko = !entry; 
    const playerName = isBlanko || isSemiBlanko ? '_____________________________' : (entry.player ? `${entry.player.firstName} ${entry.player.lastName}` : 'FEHLER: SPIELER NICHT GEFUNDEN'); 
    const clubOrTeam = isBlanko ? '_____________________________' : getAffiliationText(entry); 
    const timeText = !time ? '____:____ Uhr' : formatTime(time); 
    const laneText = !startLane ? '__' : startLane; 
    const laneOrder = getLaneOrder(isBlanko ? 0 : startLane); 
    doc.setFontSize(16); 
    doc.setFont('helvetica', 'bold'); 
    doc.text(tournament.name, 105, startY, { align: 'center' }); 
    doc.setFontSize(20); 
    doc.setFont('helvetica', 'normal'); 
    doc.text(playerName, 20, startY + 15); 
    doc.setFontSize(15); 
    doc.text(clubOrTeam, 20, startY + 22); 
    doc.setFontSize(15); 
    doc.text(`Start: ${timeText} / Bahn ${laneText}`, 190, startY + 22, { align: 'right' }); 
    const head = [['Bahn', 'Volle', 'Abr.', 'FW', 'Gesamt']]; 
    const body = laneOrder.map(laneNum => [laneNum, '', '', '', '']); 
    const foot = [['Gesamt', '', '', '', '']]; 
    autoTable(doc, { startY: startY + 30, head, body, foot, theme: 'grid', margin: { left: 15, right: 15 }, headStyles: { fontStyle: 'bold', halign: 'center', fillColor: [220, 220, 220], textColor: 20, fontSize: 10 }, footStyles: { fontStyle: 'bold', halign: 'center', valign: 'middle', fontSize: 15, fillColor: [255, 255, 255], textColor: [0, 0, 0], lineColor: [44, 62, 80], lineWidth: 0.1, minCellHeight: 20 }, bodyStyles: { minCellHeight: 15, fontSize: 10, valign: 'middle' }, columnStyles: { 0: { cellWidth: 25, fontStyle: 'bold', halign: 'center', fontSize: 14 }, 1: { cellWidth: 45, halign: 'center' }, 2: { cellWidth: 45, halign: 'center' }, 3: { cellWidth: 20, halign: 'center' }, 4: { cellWidth: 'auto', fontStyle: 'bold', halign: 'center' }, } }); 
    if (showSignatureInPdf) { 
      const finalY = doc.lastAutoTable.finalY; 
      doc.setFontSize(9); 
      doc.text('Unterschrift Spieler:', 20, finalY + 7); 
      doc.line(55, finalY + 7, 100, finalY + 7); 
      doc.text('Unterschrift Schreiber:', 110, finalY + 7); 
      doc.line(150, finalY + 7, 195, finalY + 7); 
    } 
  };
  
  const generateScoreSheetsForTimeSlot = (time, originalIndex) => { 
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' }); 
    const A4_MIDDLE = 148.5; 
    const startersForSlot = dynamicLanes.map(lane => { 
      const slotId = `slot-${originalIndex}-${lane}`; 
      const column = columns[slotId]; 
      if (column && column.entryIds.length > 0) { 
        return { entry: entries[column.entryIds[0]], startLane: lane }; 
      } 
      return { entry: null, startLane: lane }; 
    }); 
    const lanePairs = []; 
    for (let i = 0; i < startersForSlot.length; i += 2) { lanePairs.push([startersForSlot[i], startersForSlot[i+1]]); } 
    lanePairs.forEach((pair, index) => { 
      if (index > 0) doc.addPage(); 
      const [starter1, starter2] = pair; 
      if (starter1) drawScoreSheet(doc, starter1.entry, starter1.startLane, time, 15); 
      doc.setLineDashPattern([2, 2], 0); 
      doc.line(10, A4_MIDDLE, 200, A4_MIDDLE); 
      doc.setLineDashPattern([], 0); 
      if (starter2) drawScoreSheet(doc, starter2.entry, starter2.startLane, time, A4_MIDDLE + 15); 
    }); 
    doc.save(`Wurfscheine_${formatDateHeader(new Date(time).toLocaleDateString('en-CA', { timeZone: 'UTC' }))}_${formatTime(time).replace(':', '')}Uhr.pdf`); 
  };
  
  const generateBlankScoreSheets = () => { 
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' }); 
    const A4_MIDDLE = 148.5; 
    const startLanes = [1, 2, 3, 4]; 
    const lanePairs = [[startLanes[0], startLanes[1]], [startLanes[2], startLanes[3]]]; 
    lanePairs.forEach((pair, index) => { 
      if (index > 0) doc.addPage(); 
      const [lane1, lane2] = pair; 
      drawScoreSheet(doc, null, lane1, null, 15); 
      doc.setLineDashPattern([2, 2], 0); 
      doc.line(10, A4_MIDDLE, 200, A4_MIDDLE); 
      doc.setLineDashPattern([], 0); 
      if (lane2) { drawScoreSheet(doc, null, lane2, null, A4_MIDDLE + 15); } 
    }); 
    doc.save(`Wurfscheine_Blanko_${tournament.name.replace(/\s/g, '_')}.pdf`); 
  };
  
  const generateAllFilledScoreSheets = (semiBlanko = false) => { 
    if (!scheduleData || scheduleData.length === 0) { 
      return toast.warn("Es sind noch keine Spieler im Startplan platziert."); 
    } 
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' }); 
    const A4_MIDDLE = 148.5; 
    const sortedSchedule = [...scheduleData].filter(s => s.entry && s.entry._id).sort((a,b) => new Date(a.startTime) - new Date(b.startTime)); 
    for (let i = 0; i < sortedSchedule.length; i += 2) { 
      if (i > 0) doc.addPage(); 
      const scheduleEntry1 = sortedSchedule[i]; 
      const scheduleEntry2 = sortedSchedule[i+1]; 
      if (scheduleEntry1) { 
        drawScoreSheet(doc, entries[scheduleEntry1.entry._id], scheduleEntry1.startLane, scheduleEntry1.startTime, 15, { isSemiBlanko: semiBlanko }); 
      } 
      doc.setLineDashPattern([2, 2], 0); 
      doc.line(10, A4_MIDDLE, 200, A4_MIDDLE); 
      doc.setLineDashPattern([], 0); 
      if (scheduleEntry2) { 
        drawScoreSheet(doc, entries[scheduleEntry2.entry._id], scheduleEntry2.startLane, scheduleEntry2.startTime, A4_MIDDLE + 15, { isSemiBlanko: semiBlanko }); 
      } 
    } 
    const fileNameSuffix = semiBlanko ? 'Vereinszettel' : 'Alle_Wurfscheine'; 
    doc.save(`${fileNameSuffix}_${tournament.name.replace(/\s/g, '_')}.pdf`); 
  };

  if (loading || !columns || !entries || !teams) return <p>Lade Startplan...</p>;
  
  const availableDates = Array.from(groupedTimeSlots.keys());
  const filteredDays = availableDates.filter((dateKey) => dayFilter === 'all' || dateKey === dayFilter).map(dateKey => [dateKey, groupedTimeSlots.get(dateKey)]);
  const isFilterActive = searchTerm.trim() !== '' || teamFilter !== 'all';

  // KORREKTUR: Wir kombinieren die flachen Schedule-Daten mit dem vollen Entry-Objekt für das Modal
  const getModalScheduleEntry = () => {
    if (!modalData) return null;
    const scheduleItem = scheduleData.find(s => s.entry?._id === modalData.entryId);
    const fullEntryData = entries[modalData.entryId];
    return scheduleItem ? { ...scheduleItem, entry: fullEntryData } : null;
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      {modalData && ( 
        <ScoreEntryModal 
          scheduleEntry={getModalScheduleEntry()} 
          existingScore={scores[modalData.entryId]} 
          tournamentId={tournamentId} 
          onClose={() => setModalData(null)} 
          onSave={handleSaveScore} 
          onPlayerNameUpdate={handlePlayerNameUpdate} 
          availableClassifications={tournament?.classifications || []} 
          onEntryUpdate={handleEntryUpdate} 
          availableTeamClassifications={tournament?.teamClassifications || []} 
          onTeamUpdate={handleTeamUpdate} 
        /> 
      )}
      <div className="screen-container">
        <div className="navigation-header">
          <h2>Startplan für {tournament?.name}</h2>
          <Link to={`/turnier/${tournamentId}/tablet`} className="btn-primary-action" target="_blank">Tablet-Modus öffnen 📱</Link>
        </div>
        
        <div className="schedule-controls">
          <input type="text" placeholder="Spieler suchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
          <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} className="team-filter-select">
            <option value="all">Alle Mannschaften</option>
            {Object.values(teams).sort((a, b) => a.name.localeCompare(b.name)).map(team => (<option key={team._id} value={team._id}>{team.name}</option>))}
          </select>
        </div>
        
        <div className="export-controls">
          <div className="export-options-group">
            <h4>Optionen für Wurfschein-Druck</h4>
            <label><input type="checkbox" checked={showSignatureInPdf} onChange={e => setShowSignatureInPdf(e.target.checked)} /> Unterschriften anzeigen</label>
            <button onClick={() => generateAllFilledScoreSheets(true)} className="btn-secondary-action">Vereins-Zettel</button>
            <button onClick={() => generateAllFilledScoreSheets(false)} className="btn-secondary-action">Alle Starter-Zettel</button>
            <button onClick={generateBlankScoreSheets} className="btn-secondary-action">Leere Zettel (4 Bahnen)</button>
          </div>
          <div className="export-options-group">
            <h4>Optionen für Startplan-PDF</h4>
            <label><input type="checkbox" checked={showPlayerNamesInPdf} onChange={e => setShowPlayerNamesInPdf(e.target.checked)} /> Spielernamen anzeigen</label>
            <label><input type="checkbox" checked={exportOnlyOccupied} onChange={e => setExportOnlyOccupied(e.target.checked)} /> Nur belegte Zeiten</label>
            <div>
              <h5>Tage für Export auswählen:</h5>
              <div className="checkbox-group compact">
                {availableDates.map(dateKey => (
                  <div key={dateKey} className="checkbox-item">
                    <input type="checkbox" id={`export-${dateKey}`} checked={selectedExportDays.includes(dateKey)} onChange={() => handleExportDayChange(dateKey)} />
                    <label htmlFor={`export-${dateKey}`}>{formatDateHeader(dateKey)}</label>
                  </div>
                ))}
              </div>
            </div>
            <button onClick={exportScheduleToPDF} className="btn-primary-action">Startplan als PDF</button>
          </div>
        </div>
        
        <div className="schedule-grid">
          <div className="unassigned-entries">
            <h3>{columns['unassigned'].title}</h3>
            <Droppable droppableId="unassigned">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="unassigned-list">
                  {columns['unassigned'].itemIds.map((itemId, index) => { 
                    const [type, id] = itemId.split('-'); 
                    
                    if (type === 'team') { 
                      const team = teams[id]; 
                      if (!team) return null; 
                      
                      const teamEntries = Object.values(entries).filter(e => e.team?._id === id && e.player); 
                      const teamHasVisiblePlayer = teamEntries.some(e => filteredEntryIds.has(e._id)); 
                      if (isFilterActive && !teamHasVisiblePlayer) return null; 
                      
                      const isCollapsed = collapsedTeams[id] === undefined ? true : collapsedTeams[id]; 
                      
                      return (
                        <div key={itemId} className="team-group-unassigned">
                          <Draggable draggableId={itemId} index={index} isDragDisabled={!isCollapsed}>
                            {(p, s) => (
                              <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps} className={`entry-item team-item ${!isCollapsed ? 'collapsed-header' : ''} ${s.isDragging ? 'is-dragging' : ''}`}>
                                <span className={`arrow ${isCollapsed ? 'right' : 'down'}`} onClick={(e) => { e.stopPropagation(); toggleTeamCollapse(id); }}></span>
                                <strong>Mannschaft: {team.name}</strong>
                              </div>
                            )}
                          </Draggable>
                          {!isCollapsed && (
                            <div className="team-player-list">
                              {teamEntries.filter(e => filteredEntryIds.has(e._id)).map((entry) => (
                                <Draggable key={`entry-${entry._id}`} draggableId={`entry-${entry._id}`} index={index}>
                                  {(p, s) => (
                                    <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps} className={`entry-item sub-item ${s.isDragging ? 'is-dragging' : ''}`}>
                                      <strong>{entry.player.lastName}, {entry.player.firstName}</strong>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                            </div>
                          )}
                        </div>
                      ); 
                    } 
                    
                    if (type === 'entry') { 
                      if (!filteredEntryIds.has(id)) return null; 
                      const entry = entries[id]; 
                      if (!entry || !entry.player) return null; 
                      return (
                        <Draggable key={itemId} draggableId={itemId} index={index}>
                          {(p) => (
                            <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps} className="entry-item">
                              <strong>{entry.player.lastName}, {entry.player.firstName}</strong><br/>
                              <em>{getAffiliationText(entry)}</em>
                            </div>
                          )}
                        </Draggable>
                      ); 
                    } 
                    return null; 
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
          
          <div className="schedule-plan">
            <h3>Startzeiten</h3>
            <div className="filter-bar">
              <label htmlFor="day-filter">Tag filtern:</label>
              <select id="day-filter" value={dayFilter} onChange={(e) => setDayFilter(e.target.value)}>
                <option value="all">Alle Tage anzeigen</option>
                {availableDates.map(dateKey => (<option key={dateKey} value={dateKey}>{formatDateHeader(dateKey)}</option>))}
              </select>
            </div>
            
            {filteredDays.map(([dateKey, slots]) => { 
              const dayHasVisibleSlots = slots.some(({ originalIndex }) => dynamicLanes.some(lane => { 
                const slotId = `slot-${originalIndex}-${lane}`; 
                const entryId = columns[slotId]?.entryIds[0]; 
                return entryId && filteredEntryIds.has(entryId); 
              })); 
              
              if (isFilterActive && !dayHasVisibleSlots) return null; 
              const isCollapsed = !!collapsedDays[dateKey]; 
              
              return (
                <div key={dateKey} className="schedule-day-group">
                  <h4 className="clickable" onClick={() => toggleDayCollapse(dateKey)}>
                    <span className={`arrow ${isCollapsed ? 'right' : 'down'}`}></span>{formatDateHeader(dateKey)}
                  </h4>
                  {!isCollapsed && (
                    <table>
                      <thead>
                        <tr>
                          <th style={{width: '50px'}}></th>
                          <th>Zeit</th>
                          {dynamicLanes.map(lane => <th key={lane}>Bahn {lane}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {slots.map(({ time, originalIndex }) => { 
                          const rowHasVisiblePlayer = dynamicLanes.some(lane => { 
                            const slotId = `slot-${originalIndex}-${lane}`; 
                            const entryId = columns[slotId]?.entryIds[0]; 
                            return entryId && filteredEntryIds.has(entryId); 
                          }); 
                          
                          if (isFilterActive && !rowHasVisiblePlayer) return null; 
                          
                          return (
                            <tr key={time}>
                              <td>
                                <button className="btn-print-small" title={`Wurfscheine für ${formatTime(time)} Uhr drucken`} onClick={() => generateScoreSheetsForTimeSlot(time, originalIndex)}>🖨️</button>
                              </td>
                              <td>{formatTime(time)}</td>
                              {dynamicLanes.map(lane => { 
                                const slotId = `slot-${originalIndex}-${lane}`; 
                                const column = columns[slotId]; 
                                if(!column) return <td key={lane}></td>; 
                                
                                return (
                                  <td key={lane}>
                                    <Droppable droppableId={slotId} isDropDisabled={column.entryIds.length > 0}>
                                      {(provided, snapshot) => (
                                        <div ref={provided.innerRef} {...provided.droppableProps} className={`schedule-slot ${snapshot.isDraggingOver ? 'is-over' : ''}`}>
                                          {column.entryIds.map((entryId, index) => { 
                                            const entry = entries[entryId]; 
                                            if (!entry || !entry.player) return null; 
                                            const score = scores[entryId]; 
                                            const scheduleEntry = scheduleData.find(s => s.entry?._id === entryId); 
                                            const isMatch = filteredEntryIds.has(entryId); 
                                            
                                            return (
                                              <Draggable key={entry._id} draggableId={`entry-${entry._id}`} index={index}>
                                                {(p) => (
                                                  <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps} className={`entry-item scheduled ${isFilterActive && !isMatch ? 'faded' : ''}`} onClick={() => { if (scheduleEntry) setModalData({ entryId: entry._id }); }}>
                                                    <strong>{entry.player.lastName}, {entry.player.firstName}</strong><br/>
                                                    <em>{getAffiliationText(entry)}</em>
                                                    {score && <div className="score-badge">{score.totalGesamt}</div>}
                                                  </div>
                                                )}
                                              </Draggable>
                                            ); 
                                          })}
                                          {column.entryIds.length === 0 && <div className="empty-slot"></div>}
                                          {provided.placeholder}
                                        </div>
                                      )}
                                    </Droppable>
                                  </td>
                                ); 
                              })}
                            </tr>
                          ); 
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              ); 
            })}
          </div>
        </div>
      </div>
    </DragDropContext>
  );
};

export default ScheduleScreen;